import { ArrowButton } from "components"
import LanguageContext from "logic/context/language"
import useTilesStagger, {
  TilesStaggerVars,
} from "logic/hooks/use-tiles-stagger"
import { FluidImage } from "model/content"
import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react"

import styled, { css } from "styled-components"
import tw from "twin.macro"
import { breakpoint } from "styles/breakpoints"
import PhotoTile from "./PhotoTileForSlider"

type PhotoWithHeading = {
  heading: string
  photo: FluidImage
  description?: string
}
type PhotosSliderProps = {
  photosCountToShow?: number
  photosCountToMoveOnClick?: number
  photos: FluidImage[] | PhotoWithHeading[]
  photoHeightPx?: number
}

const PhotosSliderContainer = styled.div`
  ${tw`flex flex-col lg:flex-row pt-0 pb-0`}
`

const PhotoContainer = styled.div`
  ${tw`flex flex-col h-full`};
  ${({ photosCountToShow }) =>
    css`
      width: ${100 / photosCountToShow}vw;
    `}
`

const TileHeading = styled.h2<{
  isRtl: boolean
  big: boolean | undefined
}>`
  ${tw`font-condensed leading-none whitespace-no-wrap transition duration-200 text-white`};
  ${({ big }) => (big ? tw` text-3xl` : tw` text-xl`)}

  border-${({ isRtl }) => (isRtl ? "right" : "left")}-color: #FFE81D;
  border-${({ isRtl }) => (isRtl ? "right" : "left")}-width: 12px;
  margin-${({ isRtl }) => (isRtl ? "right" : "left")}: -40px;
  padding-${({ isRtl }) => (isRtl ? "right" : "left")}: 28px;
`

const PhotosSliderFullWidth = styled.div`
  width: 100vw;
  position: relative;
  margin: 0 -50vw;
  left: 50%;
  right: 50%;
  ${tw`bg-dominant pb-0 md:pb-0`};
`

const PhotosSliderList = styled.div`
  ${tw`grid gap-0 lg:gap-0 pt-0 grid-flow-col overflow-x-scroll overflow-y-hidden z-20`}
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    display: none;
  }
`

const PhotosSliderContent = styled.div`
  ${tw`flex flex-col overflow-hidden pt-0`}
`

const TileContent = styled.div<{ isRtl: boolean }>`
  ${tw`flex flex-col relative transition-all duration-200 flex-1`};
  ${tw`px-10 py-6`}
  margin-left: 1px;
`

const ArrowsContainer2 = styled.div`
  width: 100vw;
  position: absolute;
  top: ${({ photoHeightPx }) => photoHeightPx / 2 - 22}px;
  ${breakpoint("md")`top: ${({ photoHeightPx }) => photoHeightPx / 2 - 22}px;`}
`
const ArrowsContainer = styled.div<{ isRtl: boolean }>`
  ${tw`flex justify-end mx-8 md:mx-16`}
  justify-content: space-between;

  ${({ isRtl }) => isRtl && tw`flex-row-reverse`}
`

const ArrowButtonInFront = styled(ArrowButton)`
  z-index: 25;
  opacity: 80%;

  :hover {
    opacity: 100%;
  }
`

const TileDescription = styled.p<{ big: boolean | undefined }>`
  ${tw`text-white my-6 w-full flex-1 flex-row`};

  ${({ big }) => (big ? tw`my-6` : tw`my-2`)}
  ${({ big }) => !big && ``};
`

const isFullyScrolled = (el: HTMLElement) =>
  el.offsetWidth + el.scrollLeft === el.scrollWidth

const staggerOptions: TilesStaggerVars = {
  stagger: { from: "start" },
  slideInOnMobile: false,
}

export const PhotosSlider: FunctionComponent<PhotosSliderProps> = ({
  photos,
  photosCountToShow = 3,
  photosCountToMoveOnClick = 3,
  photoHeightPx = 270,
}) => {
  const { isRtl } = useContext(LanguageContext)
  const [vw, setVW] = useState(0)
  const moveRightOrLeftWidth =
    (vw / photosCountToShow) * photosCountToMoveOnClick
  const { sectionRef: tilesContainer, tilesRefs } = useTilesStagger(
    true,
    staggerOptions,
    false
  )
  const [scrollLeftDisabled, setScrollLeftDisabled] = useState(!isRtl)
  const [scrollRightDisabled, setScrollRightDisabled] = useState(isRtl)
  useEffect(() => {
    setVW(
      Math.max(
        // This needs to be in useEffect or Server Side Rendering will fail on Netlify build
        (document && document.documentElement.clientWidth) || 0,
        window.innerWidth || 0
      )
    )
    const currentTilesContainer = tilesContainer.current as HTMLElement
    const onTilesScroll = () => {
      setScrollLeftDisabled(
        isRtl
          ? isFullyScrolled(currentTilesContainer)
          : currentTilesContainer.scrollLeft === 0
      )
      setScrollRightDisabled(
        isRtl
          ? currentTilesContainer.scrollLeft === 0
          : isFullyScrolled(currentTilesContainer)
      )
    }
    currentTilesContainer.addEventListener("scroll", onTilesScroll)
    return () =>
      currentTilesContainer.removeEventListener("scroll", onTilesScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tilesContainer])

  return (
    <PhotosSliderFullWidth>
      <PhotosSliderContainer>
        <PhotosSliderContent>
          <PhotosSliderList ref={tilesContainer}>
            {photos.map((photo: PhotoWithHeading | FluidImage, i: number) =>
              "heading" in photo ? (
                <PhotoContainer photosCountToShow={photosCountToShow}>
                  <PhotoTile
                    ref={r => (tilesRefs.current[i] = r)}
                    photosCountToShow={photosCountToShow}
                    image={photo.photo}
                    key={photo.photo.fluid?.src}
                    photoHeightPx={photoHeightPx}
                  />
                  <TileContent isRtl={isRtl}>
                    <TileHeading
                      isRtl={isRtl}
                      big={undefined}
                      style={i === 0 ? { marginLeft: "-35px" } : {}}
                    >
                      {photo.heading.toUpperCase()}
                    </TileHeading>
                    {photo.description && (
                      <TileDescription big={false}>
                        {photo.description}
                      </TileDescription>
                    )}
                  </TileContent>
                </PhotoContainer>
              ) : (
                <PhotoTile
                  ref={r => (tilesRefs.current[i] = r)}
                  photosCountToShow={photosCountToShow}
                  image={photo}
                  key={photo.fluid?.src}
                  photoHeightPx={photoHeightPx}
                />
              )
            )}
          </PhotosSliderList>
        </PhotosSliderContent>
      </PhotosSliderContainer>
      <ArrowsContainer2 photoHeightPx={photoHeightPx}>
        <ArrowsContainer isRtl={isRtl}>
          <ArrowButtonInFront
            onClick={() =>
              (tilesContainer!.current.scrollLeft -= moveRightOrLeftWidth)
            }
            dir="left"
            disabled={scrollLeftDisabled}
          />
          <ArrowButtonInFront
            onClick={() =>
              (tilesContainer!.current.scrollLeft += moveRightOrLeftWidth)
            }
            dir="right"
            disabled={scrollRightDisabled}
          />
        </ArrowsContainer>
      </ArrowsContainer2>
    </PhotosSliderFullWidth>
  )
}

export default PhotosSlider
